'use client'

import { useCollectionBodyHtml } from '@/app/[locale]/collection/hooks/use-collection-body-html'
import { Button } from '@/components/ui/button'
import { useIsMobileOrTabletClient } from '@/hooks/common/use-device-type'
import { sanitize } from '@/lib/helpers/sanitize'
import { cn } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import { useEffect, useMemo, useRef, useState } from 'react'

interface CollectionSeoContentProps {
  htmlContent?: string // Tạm thời không dùng, giữ lại để sau này
  initialBodyHtml?: string
  className?: string
}

/**
 * Extract collection slug từ pathname (fallback)
 */
function getCollectionSlugFromPathname(pathname: string): string | undefined {
  // Remove locale và /collection/ prefix
  const match = pathname.match(/^\/(?:[a-z]{2}\/)?collection\/(.+)$/)
  return match ? match[1] : undefined
}

export const CollectionSeoContent = ({
  htmlContent, // Tạm thời không dùng, giữ lại để sau này
  initialBodyHtml,
  className,
}: CollectionSeoContentProps) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _unused = htmlContent // Tạm thời không dùng
  const [expanded, setExpanded] = useState(false)
  const [showToggle, setShowToggle] = useState(false)
  const [maxHeight, setMaxHeight] = useState('0px')
  const contentRef = useRef<HTMLDivElement>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const t = useTranslations('button')
  const pathname = usePathname()

  const isMobile = useIsMobileOrTabletClient()

  // Lấy slug trực tiếp từ pathname - đơn giản và nhanh
  const slug = useMemo(() => {
    return getCollectionSlugFromPathname(pathname)
  }, [pathname])

  // Fetch body HTML từ API
  const { data: bodyHtml } = useCollectionBodyHtml({
    slug,
    initialBodyHtml,
  })

  // Priority: API data > initialBodyHtml
  const displayHtml = bodyHtml || initialBodyHtml

  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const fullHeight = el.scrollHeight
    const initHeight = isMobile ? 250 : 200

    // Chỉ hiện nút toggle nếu nội dung đủ dài
    setShowToggle(fullHeight > initHeight)

    // Chỉ set max-height khi thu gọn, khi mở rộng để auto
    setMaxHeight(expanded ? 'none' : `${initHeight}px`)
  }, [displayHtml, expanded, isMobile])

  const scrollWithOffset = () => {
    const offset = 200

    const top = wrapperRef.current?.getBoundingClientRect().top ?? 0
    const scrollTop = window.scrollY || document.documentElement.scrollTop
    window.scrollTo({
      top: top + scrollTop - offset,
      behavior: 'smooth',
    })
  }

  const handleToggle = () => {
    if (expanded && wrapperRef.current) {
      scrollWithOffset()
      setTimeout(() => {
        setExpanded(false)
      }, 260)
    } else {
      setExpanded(true)
    }
  }

  if (!displayHtml) return <div className='mb-10'></div>

  return (
    <div
      className={cn(
        'relative mb-10 mt-6 bg-neutral-100 pb-4 text-sm lg:mt-[74px]',
        className
      )}
      ref={wrapperRef}
    >
      <div
        className={`relative mx-auto max-w-7xl overflow-hidden px-6 py-2 ${expanded ? '' : 'transition-[max-height] duration-500 ease-in-out'}`}
        style={{ maxHeight }}
      >
        <div
          ref={contentRef}
          className='rich-html w-full'
          dangerouslySetInnerHTML={{ __html: sanitize(displayHtml) }}
        />
        {!expanded && showToggle && (
          <div
            className='pointer-events-none absolute bottom-0 left-0 right-0 h-full'
            style={{
              background:
                'linear-gradient(180deg, rgba(242, 242, 242, 0) -28.68%, #F2F2F2 70.51%)',
            }}
          />
        )}
      </div>
      {showToggle && (
        <div
          className={
            expanded ? 'sticky bottom-4 z-[1]' : 'absolute bottom-4 left-4'
          }
        >
          <div className='text-center'>
            <Button
              variant='outline-neutral'
              onClick={handleToggle}
              className='text-sm'
            >
              {expanded ? t('collapse') : t('view_more')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
